import { useState, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Pencil,
  Trash2,
  ScrollText,
  Copy,
  ChevronDown,
  ChevronRight,
  Zap,
} from 'lucide-react';
import { apiClient } from '../lib/api';
import { useAuth } from '../store/auth';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Modal } from './ui/Modal';
import { Badge } from './ui/Badge';
import { useConfirm } from './ui/ConfirmDialog';
import { cn, formatDateTime, copyToClipboard } from '../lib/utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PresetType = 'custom' | 'facebook_capi' | 'google_ads' | 'tiktok_events' | 'pixel';
export type TriggerStatus = 'approved' | 'pending' | 'any';
export type ForwardMethod = 'GET' | 'POST';

export interface PostbackForward {
  id: number;
  publisherId: number;
  name: string;
  presetType: PresetType;
  triggerStatus: TriggerStatus;
  forwardUrl: string;
  forwardMethod: ForwardMethod;
  forwardHeaders: string | null;
  forwardBodyTemplate: string | null;
  maxRetries: number;
  isActive: boolean;
  createdAt: number;
  updatedAt: number;
}

interface ForwardingLog {
  id: number;
  forwardId: number;
  conversionId: number | null;
  clickId: string | null;
  requestUrl: string;
  requestMethod: string;
  requestBody: string | null;
  responseStatus: number | null;
  responseBody: string | null;
  durationMs: number | null;
  attempt: number;
  success: boolean;
  error: string | null;
  createdAt: number;
}

interface Publisher {
  id: number;
  subId: string;
  displayName: string | null;
}

// ---------------------------------------------------------------------------
// Preset templates
// ---------------------------------------------------------------------------

const PRESET_LABELS: Record<PresetType, string> = {
  custom: 'Custom',
  facebook_capi: 'Facebook CAPI',
  google_ads: 'Google Ads',
  tiktok_events: 'TikTok Events',
  pixel: 'Pixel',
};

const PRESET_COLORS: Record<PresetType, string> = {
  custom: 'border-neon-cyan/40 text-neon-cyan bg-neon-cyan/5',
  facebook_capi: 'border-blue-400/40 text-blue-400 bg-blue-400/5',
  google_ads: 'border-neon-yellow/40 text-neon-yellow bg-neon-yellow/5',
  tiktok_events: 'border-neon-magenta/40 text-neon-magenta bg-neon-magenta/5',
  pixel: 'border-neon-green/40 text-neon-green bg-neon-green/5',
};

interface PresetTemplate {
  url: string;
  method: ForwardMethod;
  body: string;
  helperText?: string;
}

const PRESET_TEMPLATES: Record<PresetType, PresetTemplate> = {
  custom: {
    url: '',
    method: 'GET',
    body: '',
  },
  facebook_capi: {
    url: 'https://graph.facebook.com/v18.0/{pixel_id}/events?access_token={access_token}',
    method: 'POST',
    body: JSON.stringify(
      {
        data: [
          {
            event_name: 'Purchase',
            event_time: '{timestamp}',
            action_source: 'website',
            user_data: { client_ip_address: '', client_user_agent: '' },
            custom_data: { value: '{revenue}', currency: 'USD', order_id: '{click_id}' },
          },
        ],
      },
      null,
      2,
    ),
    helperText:
      'Replace {pixel_id} with your Facebook Pixel ID and {access_token} with your System User access token in the URL above.',
  },
  google_ads: {
    url: 'https://www.googleadservices.com/pagead/conversion/{conversion_id}/?label={label}&value={revenue}&currency_code=USD&oid={click_id}',
    method: 'GET',
    body: '',
    helperText: 'Replace {conversion_id} and {label} with your Google Ads conversion tag values.',
  },
  tiktok_events: {
    url: 'https://business-api.tiktok.com/open_api/v1.3/event/track/',
    method: 'POST',
    body: JSON.stringify(
      {
        pixel_code: '{pixel_code}',
        event: 'CompletePayment',
        timestamp: '{timestamp}',
        context: { ip: '', user_agent: '' },
        properties: { value: '{revenue}', currency: 'USD', order_id: '{click_id}' },
      },
      null,
      2,
    ),
    helperText:
      'Add your TikTok Authorization header in the Headers field. Replace {pixel_code} with your TikTok Pixel code.',
  },
  pixel: {
    url: 'https://example.com/pixel?click_id={click_id}&revenue={revenue}&status={status}',
    method: 'GET',
    body: '',
    helperText: 'Fires as an image pixel (GET). No response body is expected.',
  },
};

const MACRO_HINT =
  'Available macros: {click_id}  {sub_id}  {revenue}  {payout}  {offer_id}  {country}  {event}  {status}  {timestamp}';

// ---------------------------------------------------------------------------
// ForwardingTable props
// ---------------------------------------------------------------------------

export interface ForwardingTableProps {
  mode: 'admin' | 'user';
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ForwardingTable({ mode }: ForwardingTableProps) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { user } = useAuth();

  const [filterText, setFilterText] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PostbackForward | null>(null);
  const [logsTarget, setLogsTarget] = useState<PostbackForward | null>(null);

  const unlinked = mode === 'user' && user?.publisherId == null;

  const forwardingQuery = useQuery({
    queryKey: ['forwarding', mode],
    queryFn: () => apiClient.get<PostbackForward[]>('/api/v1/forwarding'),
  });

  const publishersQuery = useQuery({
    queryKey: ['publishers'],
    queryFn: () => apiClient.get<Publisher[]>('/api/v1/publishers'),
    enabled: mode === 'admin',
  });

  const createMutation = useMutation({
    mutationFn: (d: Partial<PostbackForward>) => apiClient.post('/api/v1/forwarding', d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['forwarding'] });
      setCreateOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...d }: Partial<PostbackForward> & { id: number }) =>
      apiClient.patch(`/api/v1/forwarding/${id}`, d),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['forwarding'] });
      setEditTarget(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/api/v1/forwarding/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forwarding'] }),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      apiClient.patch(`/api/v1/forwarding/${id}`, { isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forwarding'] }),
  });

  const rows = (forwardingQuery.data ?? []).filter((f) => {
    if (!filterText) return true;
    const q = filterText.toLowerCase();
    return (
      f.name.toLowerCase().includes(q) ||
      f.forwardUrl.toLowerCase().includes(q) ||
      PRESET_LABELS[f.presetType].toLowerCase().includes(q)
    );
  });

  const publisherMap = Object.fromEntries(
    (publishersQuery.data ?? []).map((p) => [p.id, p]),
  );

  const colSpan = mode === 'admin' ? 9 : 8;

  if (unlinked) {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHeader mode={mode} count={0} onAdd={() => {}} />
        <Card className="text-center py-16">
          <Zap className="mx-auto mb-3 text-neon-yellow" size={40} />
          <p className="font-display text-sm uppercase tracking-wider text-neon-yellow">
            Account Not Linked
          </p>
          <p className="text-xs text-text-tertiary font-mono mt-2">
            Your account is not linked to a publisher. Contact admin.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader mode={mode} count={rows.length} onAdd={() => setCreateOpen(true)} />

      {/* Filter */}
      <div className="max-w-xs">
        <Input
          placeholder="Filter by name, URL, preset…"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
      </div>

      <Card className="p-0">
        <table className="table-cyber">
          <thead>
            <tr>
              {mode === 'admin' && <th>Publisher</th>}
              <th>Name</th>
              <th>Preset</th>
              <th>Trigger</th>
              <th>Forward URL</th>
              <th>Method</th>
              <th>Active</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => (
              <ForwardRow
                key={f.id}
                forward={f}
                mode={mode}
                publisher={publisherMap[f.publisherId]}
                onEdit={() => setEditTarget(f)}
                onLogs={() => setLogsTarget(f)}
                onDelete={() => {
                  confirm({
                    title: `Delete forwarding "${f.name}"?`,
                    description: 'This action cannot be undone. The forwarding rule + all its logs will be removed.',
                    variant: 'danger',
                    actions: [{ label: 'Delete', variant: 'danger', result: 'go' }],
                  }).then((r) => { if (r === 'go') deleteMutation.mutate(f.id); });
                }}
                onToggleActive={(isActive) => toggleActiveMutation.mutate({ id: f.id, isActive })}
              />
            ))}
            {rows.length === 0 && !forwardingQuery.isLoading && (
              <tr>
                <td colSpan={colSpan} className="text-center py-16 text-text-tertiary">
                  <Zap className="mx-auto mb-3" size={32} />
                  <p className="font-mono text-xs">no forwarding rules configured</p>
                </td>
              </tr>
            )}
            {forwardingQuery.isLoading && (
              <tr>
                <td colSpan={colSpan} className="text-center py-12 text-text-tertiary">
                  <span className="font-mono text-xs">loading…</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* Create modal */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="ADD FORWARDING RULE"
        description="// Configure S2S postback relay"
        size="xl"
      >
        <ForwardForm
          mode={mode}
          publishers={publishersQuery.data ?? []}
          defaultPublisherId={mode === 'user' ? (user?.publisherId ?? undefined) : undefined}
          onSubmit={(d) => createMutation.mutate(d)}
          onCancel={() => setCreateOpen(false)}
          loading={createMutation.isPending}
          error={createMutation.error?.message}
        />
      </Modal>

      {/* Edit modal */}
      <Modal
        open={editTarget != null}
        onClose={() => setEditTarget(null)}
        title="EDIT FORWARDING RULE"
        size="xl"
      >
        {editTarget && (
          <ForwardForm
            mode={mode}
            publishers={publishersQuery.data ?? []}
            defaultPublisherId={mode === 'user' ? (user?.publisherId ?? undefined) : undefined}
            initial={editTarget}
            onSubmit={(d) => updateMutation.mutate({ id: editTarget.id, ...d })}
            onCancel={() => setEditTarget(null)}
            loading={updateMutation.isPending}
            error={updateMutation.error?.message}
          />
        )}
      </Modal>

      {/* Logs modal */}
      <Modal
        open={logsTarget != null}
        onClose={() => setLogsTarget(null)}
        title={logsTarget ? `FORWARDING LOGS: ${logsTarget.name}` : 'FORWARDING LOGS'}
        description="// Last 100 delivery attempts"
        size="xl"
      >
        {logsTarget && <LogsPanel forwardId={logsTarget.id} />}
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page header
// ---------------------------------------------------------------------------

function PageHeader({
  mode,
  count,
  onAdd,
}: {
  mode: 'admin' | 'user';
  count: number;
  onAdd: () => void;
}) {
  return (
    <div className="flex justify-between items-start">
      <div>
        <h1 className="font-display text-2xl font-bold uppercase tracking-widest">
          {mode === 'admin' ? 'POSTBACK FORWARDING' : 'MY FORWARDING'}
        </h1>
        <p className="text-sm text-text-secondary font-mono">
          {mode === 'admin'
            ? '// S2S relay to Facebook CAPI / Google Ads / TikTok / custom'
            : '// forward your conversions to your own ad platforms'}
        </p>
        <p className="text-xs text-text-tertiary font-mono mt-0.5">// {count} rules</p>
      </div>
      <Button icon={<Plus size={14} />} variant="magenta" onClick={onAdd}>
        Add Forwarding
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table row
// ---------------------------------------------------------------------------

function ForwardRow({
  forward,
  mode,
  publisher,
  onEdit,
  onLogs,
  onDelete,
  onToggleActive,
}: {
  forward: PostbackForward;
  mode: 'admin' | 'user';
  publisher?: Publisher;
  onEdit: () => void;
  onLogs: () => void;
  onDelete: () => void;
  onToggleActive: (v: boolean) => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await copyToClipboard(forward.forwardUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const truncated =
    forward.forwardUrl.length > 40
      ? forward.forwardUrl.slice(0, 40) + '…'
      : forward.forwardUrl;

  return (
    <tr>
      {mode === 'admin' && (
        <td className="font-mono text-xs text-text-secondary">
          {publisher ? (
            <span title={publisher.displayName ?? undefined}>
              <span className="text-neon-cyan">{publisher.subId}</span>
              {publisher.displayName && (
                <span className="text-text-tertiary ml-1">({publisher.displayName})</span>
              )}
            </span>
          ) : (
            <span className="text-text-tertiary">#{forward.publisherId}</span>
          )}
        </td>
      )}
      <td className="font-mono text-xs">{forward.name}</td>
      <td>
        <span className={cn('badge', PRESET_COLORS[forward.presetType])}>
          {PRESET_LABELS[forward.presetType]}
        </span>
      </td>
      <td>
        <span
          className={cn(
            'badge',
            forward.triggerStatus === 'approved'
              ? 'badge-live'
              : forward.triggerStatus === 'pending'
                ? 'badge border-neon-yellow text-neon-yellow bg-neon-yellow/5'
                : 'badge border-neon-cyan/40 text-neon-cyan bg-neon-cyan/5',
          )}
        >
          {forward.triggerStatus.toUpperCase()}
        </span>
      </td>
      <td>
        <div className="flex items-center gap-1.5">
          <code
            className="font-mono text-[10px] text-text-secondary max-w-[180px] truncate"
            title={forward.forwardUrl}
          >
            {truncated}
          </code>
          <button
            onClick={handleCopy}
            className="text-text-tertiary hover:text-neon-cyan transition-colors flex-shrink-0"
            title="Copy URL"
          >
            <Copy size={12} />
          </button>
          {copied && <span className="text-[9px] text-neon-green font-mono">copied!</span>}
        </div>
      </td>
      <td>
        <span
          className={cn(
            'badge',
            forward.forwardMethod === 'POST'
              ? 'border-neon-magenta/40 text-neon-magenta bg-neon-magenta/5'
              : 'border-neon-cyan/40 text-neon-cyan bg-neon-cyan/5',
          )}
        >
          {forward.forwardMethod}
        </span>
      </td>
      <td>
        <button
          onClick={() => onToggleActive(!forward.isActive)}
          className={cn(
            'w-10 h-5 rounded-full relative transition-colors',
            forward.isActive ? 'bg-neon-green/30' : 'bg-bg-tertiary',
          )}
          title={forward.isActive ? 'Disable' : 'Enable'}
        >
          <span
            className={cn(
              'absolute top-0.5 w-4 h-4 rounded-full transition-all',
              forward.isActive ? 'left-5 bg-neon-green' : 'left-0.5 bg-text-tertiary',
            )}
          />
        </button>
      </td>
      <td className="text-xs text-text-tertiary font-mono">{formatDateTime(forward.createdAt)}</td>
      <td>
        <div className="flex gap-1 justify-end">
          <Button
            variant="ghost"
            size="sm"
            icon={<ScrollText size={12} />}
            onClick={onLogs}
            title="View logs"
          />
          <Button
            variant="ghost"
            size="sm"
            icon={<Pencil size={12} />}
            onClick={onEdit}
            title="Edit"
          />
          <Button
            variant="danger"
            size="sm"
            icon={<Trash2 size={12} />}
            onClick={onDelete}
            title="Delete"
          />
        </div>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Create / Edit form
// ---------------------------------------------------------------------------

interface ForwardFormProps {
  mode: 'admin' | 'user';
  publishers: Publisher[];
  defaultPublisherId?: number;
  initial?: PostbackForward;
  onSubmit: (d: Partial<PostbackForward>) => void;
  onCancel: () => void;
  loading: boolean;
  error?: string;
}

function ForwardForm({
  mode,
  publishers,
  defaultPublisherId,
  initial,
  onSubmit,
  onCancel,
  loading,
  error,
}: ForwardFormProps) {
  const [publisherId, setPublisherId] = useState<number | undefined>(
    initial?.publisherId ?? defaultPublisherId,
  );
  const [name, setName] = useState(initial?.name ?? '');
  const [preset, setPreset] = useState<PresetType>(initial?.presetType ?? 'custom');
  const [triggerStatus, setTriggerStatus] = useState<TriggerStatus>(
    initial?.triggerStatus ?? 'approved',
  );
  const [url, setUrl] = useState(initial?.forwardUrl ?? '');
  const [method, setMethod] = useState<ForwardMethod>(initial?.forwardMethod ?? 'GET');
  const [headers, setHeaders] = useState(initial?.forwardHeaders ?? '');
  const [bodyTemplate, setBodyTemplate] = useState(initial?.forwardBodyTemplate ?? '');
  const [maxRetries, setMaxRetries] = useState(initial?.maxRetries ?? 3);

  const applyPreset = (p: PresetType) => {
    setPreset(p);
    const t = PRESET_TEMPLATES[p];
    setUrl(t.url);
    setMethod(t.method);
    setBodyTemplate(t.body);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      publisherId: mode === 'admin' ? publisherId : defaultPublisherId,
      name,
      presetType: preset,
      triggerStatus,
      forwardUrl: url,
      forwardMethod: method,
      forwardHeaders: headers || null,
      forwardBodyTemplate: bodyTemplate || null,
      maxRetries,
    });
  };

  const helperText = PRESET_TEMPLATES[preset].helperText;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Publisher selector — admin only */}
      {mode === 'admin' && (
        <div>
          <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">
            Publisher
          </label>
          <select
            className="input-cyber"
            value={publisherId ?? ''}
            onChange={(e) => setPublisherId(Number(e.target.value))}
            required
          >
            <option value="">Select publisher…</option>
            {publishers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.subId}{p.displayName ? ` — ${p.displayName}` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      <Input
        label="Rule Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Facebook CAPI Production"
        required
      />

      {/* Preset selector */}
      <div>
        <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">
          Preset
        </label>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(PRESET_LABELS) as PresetType[]).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => applyPreset(p)}
              className={cn(
                'px-3 py-1.5 text-xs font-display uppercase tracking-wider rounded border transition-all',
                preset === p
                  ? PRESET_COLORS[p] + ' opacity-100'
                  : 'border-border-subtle text-text-secondary hover:border-text-secondary opacity-70',
              )}
            >
              {PRESET_LABELS[p]}
            </button>
          ))}
        </div>
        {helperText && (
          <p className="text-[10px] text-neon-yellow font-mono mt-2 leading-relaxed">
            {helperText}
          </p>
        )}
      </div>

      {/* Trigger status */}
      <div>
        <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">
          Trigger on Status
        </label>
        <select
          className="input-cyber"
          value={triggerStatus}
          onChange={(e) => setTriggerStatus(e.target.value as TriggerStatus)}
        >
          <option value="approved">Approved</option>
          <option value="pending">Pending</option>
          <option value="any">Any</option>
        </select>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <div className="col-span-3">
          <Input
            label="Forward URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
            required
          />
        </div>
        <div>
          <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">
            Method
          </label>
          <select
            className="input-cyber"
            value={method}
            onChange={(e) => setMethod(e.target.value as ForwardMethod)}
          >
            <option value="GET">GET</option>
            <option value="POST">POST</option>
          </select>
        </div>
      </div>

      {/* Macros hint */}
      <p className="text-[10px] font-mono text-text-tertiary leading-relaxed">{MACRO_HINT}</p>

      {/* Headers */}
      <div>
        <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">
          Additional Headers (JSON)
        </label>
        <textarea
          className="input-cyber font-mono text-xs w-full resize-y min-h-[64px]"
          value={headers}
          onChange={(e) => setHeaders(e.target.value)}
          placeholder={'{"Authorization": "Bearer token123"}'}
        />
      </div>

      {/* Body template — shown only for POST */}
      {method === 'POST' && (
        <div>
          <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">
            Body Template (JSON)
          </label>
          <textarea
            className="input-cyber font-mono text-xs w-full resize-y min-h-[120px]"
            value={bodyTemplate}
            onChange={(e) => setBodyTemplate(e.target.value)}
            placeholder="{}"
          />
          <p className="text-[10px] font-mono text-text-tertiary mt-1">{MACRO_HINT}</p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5">
            Max Retries
          </label>
          <input
            type="number"
            min={0}
            max={10}
            className="input-cyber"
            value={maxRetries}
            onChange={(e) => setMaxRetries(Number(e.target.value))}
          />
        </div>
      </div>

      {error && <p className="text-xs text-neon-red font-mono">{error}</p>}

      <div className="flex gap-2 justify-end pt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="magenta" loading={loading} icon={<Plus size={14} />}>
          {initial ? 'Save Changes' : 'Create Rule'}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Logs panel
// ---------------------------------------------------------------------------

function LogsPanel({ forwardId }: { forwardId: number }) {
  const [expanded, setExpanded] = useState<number | null>(null);

  const logsQuery = useQuery({
    queryKey: ['forwarding-logs', forwardId],
    queryFn: () => apiClient.get<ForwardingLog[]>(`/api/v1/forwarding/${forwardId}/logs`),
  });

  const logs = logsQuery.data ?? [];

  if (logsQuery.isLoading) {
    return <p className="text-center text-text-tertiary font-mono text-xs py-8">loading…</p>;
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-12 text-text-tertiary">
        <ScrollText className="mx-auto mb-3" size={32} />
        <p className="font-mono text-xs">no delivery attempts yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <table className="table-cyber">
        <thead>
          <tr>
            <th className="w-6"></th>
            <th>Time</th>
            <th>Attempt</th>
            <th>Success</th>
            <th>HTTP Status</th>
            <th>Duration</th>
            <th>Click ID</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <Fragment key={log.id}>
              <tr
                className="cursor-pointer hover:bg-bg-tertiary/40"
                onClick={() => setExpanded(expanded === log.id ? null : log.id)}
              >
                <td>
                  {expanded === log.id ? (
                    <ChevronDown size={12} className="text-text-tertiary" />
                  ) : (
                    <ChevronRight size={12} className="text-text-tertiary" />
                  )}
                </td>
                <td className="text-xs font-mono text-text-secondary">
                  {formatDateTime(log.createdAt)}
                </td>
                <td className="font-mono text-xs">{log.attempt}</td>
                <td>
                  <Badge
                    variant={log.success ? 'live' : 'error'}
                  >
                    {log.success ? 'OK' : 'FAIL'}
                  </Badge>
                </td>
                <td className="font-mono text-xs">
                  {log.responseStatus != null ? (
                    <span
                      className={cn(
                        log.responseStatus >= 200 && log.responseStatus < 300
                          ? 'text-neon-green'
                          : log.responseStatus >= 400
                            ? 'text-neon-red'
                            : 'text-neon-yellow',
                      )}
                    >
                      {log.responseStatus}
                    </span>
                  ) : (
                    <span className="text-text-tertiary">—</span>
                  )}
                </td>
                <td className="font-mono text-xs text-text-secondary">
                  {log.durationMs != null ? `${log.durationMs}ms` : '—'}
                </td>
                <td>
                  {log.clickId ? (
                    <code className="font-mono text-[10px] text-text-secondary">
                      {log.clickId.slice(0, 12)}…
                    </code>
                  ) : (
                    <span className="text-text-tertiary">—</span>
                  )}
                </td>
              </tr>
              {expanded === log.id && (
                <tr key={`${log.id}-detail`}>
                  <td colSpan={7} className="bg-bg-secondary/60 px-4 py-3">
                    <div className="space-y-3 font-mono text-xs">
                      <div>
                        <span className="text-text-tertiary uppercase text-[9px] tracking-wider">
                          Request URL
                        </span>
                        <pre className="text-neon-cyan mt-0.5 whitespace-pre-wrap break-all">
                          {log.requestMethod} {log.requestUrl}
                        </pre>
                      </div>
                      {log.requestBody && (
                        <div>
                          <span className="text-text-tertiary uppercase text-[9px] tracking-wider">
                            Request Body
                          </span>
                          <pre className="text-text-secondary mt-0.5 whitespace-pre-wrap break-all max-h-32 overflow-y-auto">
                            {log.requestBody}
                          </pre>
                        </div>
                      )}
                      {log.responseBody && (
                        <div>
                          <span className="text-text-tertiary uppercase text-[9px] tracking-wider">
                            Response Body
                          </span>
                          <pre className="text-text-secondary mt-0.5 whitespace-pre-wrap break-all max-h-32 overflow-y-auto">
                            {log.responseBody}
                          </pre>
                        </div>
                      )}
                      {log.error && (
                        <div>
                          <span className="text-text-tertiary uppercase text-[9px] tracking-wider">
                            Error
                          </span>
                          <pre className="text-neon-red mt-0.5 whitespace-pre-wrap break-all">
                            {log.error}
                          </pre>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
